/**
 * 🪝 Wick rejection, "possible reversal" (issue #586): a closed candle with one long wick
 * that the bar rejected (it closed back in the other half of its range).
 * Display only: no alert, no push. Separate from Pinning Volume (which skips
 * bars within 19 pts of HOD/LOD and needs 2x volume, so it misses exactly the
 * near-LOD rejection wicks this marks).
 *
 * Rule, closed chart bars only (the forming bar never gets one), on the bars
 * the chart draws (`wickInputBars`: continuous-open on `.VEST`, raw on CME):
 *   wick    = the larger of the two wicks (a tie is no mark)
 *   wick   >= minWickPct % of the bar range
 *   wick   >= minWickAtr x ATR, ATR = mean high-low of the previous 14 bars
 *             (needs 5+ earlier bars); range >= 0.5 x ATR (tiny bars skipped)
 *   close  in the opposite half: lower wick -> close >= midpoint,
 *           upper wick -> close <= midpoint
 * Lower wick = rejection of lower prices, marker under the low (reversal
 * up); upper wick = marker over the high (reversal down).
 *
 * Grade: STRONG (bigger, amber halo) when any factor fired, else base:
 *   level      the wick swept a level and the body stayed on the other side:
 *              lower wick low < L <= min(open, close) (upper mirrored). Levels
 *              as they stood BEFORE the bar, all derived from the chart's own
 *              bars so a reload marks exactly what live did: RTH HOD/LOD
 *              (08:30-15:15 CT, the engine's window), PDH/PDL (previous
 *              trading day's RTH), ONH/ONL (17:00-08:30 CT; running before
 *              08:30, fixed after) and VWAP (the chart's VWAP at the previous
 *              bar). GEX / volume-profile / weekly levels are left out on
 *              purpose: the chart only has today's snapshot of them, so on
 *              older bars they would mark against levels that did not exist.
 *   spike      🐋 Vest or 🦈 CME volume spike on the same bar (the exact 🐋/🦈
 *              results, passed in).
 *   absorption CME bar delta (ask - bid prints) against the wick: lower wick
 *              with net selling, upper wick with net buying, |delta| >= 10%
 *              of the bar's volume. The aggressors leaned into the wick and
 *              price still came back. Skipped when the bar has no delta.
 *
 * Lifecycle (closed bars only, so reload == live):
 *   pending    drawn at half opacity until resolved;
 *   confirmed  a later bar, within confirmBars bars, CLOSES beyond the body in
 *              the reversal direction (lower wick: close > max(open, close));
 *              full opacity from then on;
 *   invalidated a later bar, within confirmBars bars and before any
 *              confirmation, trades beyond the wick tip ("broken"). Drawn
 *              very small and heavily faded, never haloed (brokenHooks
 *              'faded', the default), or not at all ('hide'). On a bar that
 *              does both, the tip break wins;
 *   expired    confirmBars bars passed with neither: stays at half opacity.
 */
import { chicagoParts } from './mocLevel';
import type { MarkerSide } from './vestSpikes';
import { isVestFeedSymbol, priceSeriesCandles } from './vestFeed';

export const WICK_EMOJIS = ['🪝', '📌', '🔄', '⚓', '🎣'] as const;

/** Broken (invalidated) hooks: drawn small and faded, or hidden. */
export const WICK_BROKEN_MODES = ['faded', 'hide'] as const;
export type WickBrokenMode = (typeof WICK_BROKEN_MODES)[number];

export interface WickSettings {
  /** Glyph drawn at the wick tip, one of WICK_EMOJIS. */
  emoji: string;
  /** Rejection wick as % of the bar range. */
  minWickPct: number;
  /** Rejection wick in ATRs. */
  minWickAtr: number;
  /** Bars a later close has to confirm the reversal. */
  confirmBars: number;
  /** Only show marks whose wick swept a level (all of those are strong). */
  requireLevel: boolean;
  /** Invalidated hooks: 'faded' = very small + faint at the tip, 'hide' = not drawn. */
  brokenHooks: WickBrokenMode;
}

/**
 * Tuned on 2m MNQ, 23 RTH sessions 2026-09-21..10-21 (docs/kb/
 * 10-vest-market-data.md, "Wick rejection"): 70% / 0.55 ATR draws ~4.1 base
 * and ~2.6 strong marks per RTH session (9.8 qualify, 32% are invalidated).
 */
export const DEFAULT_WICK_SETTINGS: WickSettings = {
  emoji: '🪝',
  minWickPct: 70,
  minWickAtr: 0.55,
  confirmBars: 3,
  requireLevel: false,
  brokenHooks: 'faded',
};

export const WICK_PCT_RANGE = { min: 30, max: 95 } as const;
export const WICK_ATR_RANGE = { min: 0.1, max: 5 } as const;
export const WICK_CONFIRM_RANGE = { min: 1, max: 20 } as const;
export const WICK_ATR_PERIOD = 14;
const WICK_ATR_MIN_BARS = 5;
/** Bars with a range under this many ATRs are skipped outright. */
export const WICK_MIN_RANGE_ATR = 0.5;
/** Absorption: |CME delta| must be at least this share of the bar's volume. */
export const WICK_ABSORPTION_MIN_FRAC = 0.1;
/** Previous-bar VWAP only counts when that bar is this close in time. */
const VWAP_MAX_GAP_SEC = 3600;

function clampNum(v: unknown, range: { min: number; max: number }, dflt: number, round = false): number {
  const x = v == null || v === '' ? Number.NaN : Number(v);
  if (!Number.isFinite(x)) return dflt;
  const c = Math.min(range.max, Math.max(range.min, x));
  return round ? Math.round(c) : Math.round(c * 100) / 100;
}

export function clampWickSettings(s: Partial<WickSettings> | null | undefined): WickSettings {
  const d = DEFAULT_WICK_SETTINGS;
  return {
    emoji: typeof s?.emoji === 'string' && (WICK_EMOJIS as readonly string[]).includes(s.emoji) ? s.emoji : d.emoji,
    minWickPct: clampNum(s?.minWickPct, WICK_PCT_RANGE, d.minWickPct, true),
    minWickAtr: clampNum(s?.minWickAtr, WICK_ATR_RANGE, d.minWickAtr),
    confirmBars: clampNum(s?.confirmBars, WICK_CONFIRM_RANGE, d.confirmBars, true),
    requireLevel: typeof s?.requireLevel === 'boolean' ? s.requireLevel : d.requireLevel,
    // Blobs saved before this setting existed have no key: they get the default.
    brokenHooks: (WICK_BROKEN_MODES as readonly unknown[]).includes(s?.brokenHooks) ? (s!.brokenHooks as WickBrokenMode) : d.brokenHooks,
  };
}

export interface WickBar {
  /** Chart bar open, seconds. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

/**
 * The bars the 🪝 detector runs on for `symbol`: the bars the price series
 * DRAWS. A Vest chart feed (`*.VEST`) draws continuous-open candles (#626,
 * `vestFeed.ts::continuousOpen`: open = previous close, high/low widened), and
 * the glyph is pinned to the drawn bar's high/low, so the rule must judge the
 * drawn wick: on raw BTC trade candles only 46% of hooked bars passed the
 * rule on the bar actually shown and 4% had no visible wick at all. This is a
 * deliberate display exception: every other detector and strategy stays on
 * the raw trade candles. Drawn bar i depends only on raw bars i and i-1, so
 * a closed bar reads the same here as `priceSeriesLastBar` drew it while it
 * was forming (no flip between raw and drawn when it closes).
 * Every other symbol (CME NQ/MNQ/ES/MES, Sierra): the SAME array, untouched.
 */
export function wickInputBars<T extends WickBar>(symbol: string | null | undefined, bars: T[]): T[] {
  if (!isVestFeedSymbol(symbol)) return bars;
  return priceSeriesCandles(symbol, bars);
}

/**
 * Chart bars that are closed: every bar that ends at or before the start of
 * the chart bar holding the forming base candle (the 🦈 `closedCmeRelVols`
 * rule, no browser clock), else at or before `nowMs`.
 */
export function closedWickBars<T extends WickBar>(
  bars: readonly T[],
  formingMs: number | null | undefined,
  timeframeMinutes: number,
  nowMs: number = Date.now(),
): T[] {
  const intervalMs = Math.max(60_000, Math.round(timeframeMinutes * 60_000));
  const cutoff = typeof formingMs === 'number' && Number.isFinite(formingMs) ? Math.floor(formingMs / intervalMs) * intervalMs : nowMs;
  return bars.filter((b) => b.time * 1000 + intervalMs <= cutoff);
}

export type WickLevelName = 'HOD' | 'LOD' | 'PDH' | 'PDL' | 'ONH' | 'ONL' | 'VWAP';
export interface WickLevel {
  name: WickLevelName;
  price: number;
}

// ── Chicago wall clock, cached per UTC hour (DST moves on hour boundaries) ──
const offsetCache = new Map<number, number>();
function ctOffsetMs(ms: number): number {
  const hour = Math.floor(ms / 3_600_000);
  let off = offsetCache.get(hour);
  if (off === undefined) {
    const at = hour * 3_600_000;
    const p = chicagoParts(at);
    off = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - at;
    if (offsetCache.size > 50_000) offsetCache.clear();
    offsetCache.set(hour, off);
  }
  return off;
}

/** Minutes after CT midnight, and the CME trading day (17:00 CT roll) as a day number. */
function ctClock(ms: number): { minute: number; tradingDay: number } {
  const wall = ms + ctOffsetMs(ms);
  const dayNum = Math.floor(wall / 86_400_000);
  const minute = Math.floor((wall - dayNum * 86_400_000) / 60_000);
  return { minute, tradingDay: minute >= ROLL_MIN ? dayNum + 1 : dayNum };
}

const RTH_START_MIN = 8 * 60 + 30;
const RTH_END_MIN = 15 * 60 + 15;
const ROLL_MIN = 17 * 60;

/**
 * Levels as they stood before each bar (index-aligned with `bars`, which must
 * be ascending). `vwap` = the chart's VWAP by bar time (seconds).
 */
export function levelsBeforeBars(bars: readonly WickBar[], vwap?: ReadonlyMap<number, number>): WickLevel[][] {
  const out: WickLevel[][] = [];
  let day: number | null = null;
  let onHi = -Infinity;
  let onLo = Infinity;
  let rthHi = -Infinity;
  let rthLo = Infinity;
  let pd: { high: number; low: number } | null = null;
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const { minute, tradingDay } = ctClock(b.time * 1000);
    if (tradingDay !== day) {
      if (day !== null && Number.isFinite(rthHi)) pd = { high: rthHi, low: rthLo };
      day = tradingDay;
      onHi = -Infinity;
      onLo = Infinity;
      rthHi = -Infinity;
      rthLo = Infinity;
    }
    const lv: WickLevel[] = [];
    if (Number.isFinite(rthHi)) lv.push({ name: 'HOD', price: rthHi }, { name: 'LOD', price: rthLo });
    if (pd) lv.push({ name: 'PDH', price: pd.high }, { name: 'PDL', price: pd.low });
    if (Number.isFinite(onHi)) lv.push({ name: 'ONH', price: onHi }, { name: 'ONL', price: onLo });
    if (vwap && i > 0 && b.time - bars[i - 1].time <= VWAP_MAX_GAP_SEC) {
      const v = vwap.get(bars[i - 1].time);
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) lv.push({ name: 'VWAP', price: v });
    }
    out.push(lv);
    const isOvernight = minute < RTH_START_MIN || minute >= ROLL_MIN;
    if (isOvernight) {
      onHi = Math.max(onHi, b.high);
      onLo = Math.min(onLo, b.low);
    } else if (minute < RTH_END_MIN) {
      rthHi = Math.max(rthHi, b.high);
      rthLo = Math.min(rthLo, b.low);
    }
  }
  return out;
}

/** Mean high-low of the `period` bars before index i, or null with too little history. */
export function wickAtr(bars: readonly WickBar[], i: number, period = WICK_ATR_PERIOD): number | null {
  const from = Math.max(0, i - period);
  const n = i - from;
  if (n < WICK_ATR_MIN_BARS) return null;
  let sum = 0;
  for (let k = from; k < i; k++) sum += bars[k].high - bars[k].low;
  const atr = sum / n;
  return atr > 0 ? atr : null;
}

export interface WickGeometry {
  /** 'below' = lower wick (reversal up), 'above' = upper wick (reversal down). */
  side: MarkerSide;
  tip: number;
  wick: number;
  range: number;
  wickPct: number;
  bodyHigh: number;
  bodyLow: number;
}

/** The bar's rejection wick when it passes the rule, else null. */
export function wickGeometry(bar: WickBar, atr: number | null, s: Pick<WickSettings, 'minWickPct' | 'minWickAtr'>): WickGeometry | null {
  if (atr === null || !(atr > 0)) return null;
  const range = bar.high - bar.low;
  if (!(range > 0) || range < WICK_MIN_RANGE_ATR * atr) return null;
  const bodyHigh = Math.max(bar.open, bar.close);
  const bodyLow = Math.min(bar.open, bar.close);
  const lower = bodyLow - bar.low;
  const upper = bar.high - bodyHigh;
  if (lower === upper) return null;
  const isLower = lower > upper;
  const wick = isLower ? lower : upper;
  const wickPct = (wick / range) * 100;
  if (wickPct < s.minWickPct || wick < s.minWickAtr * atr) return null;
  const mid = (bar.high + bar.low) / 2;
  if (isLower ? bar.close < mid : bar.close > mid) return null;
  return { side: isLower ? 'below' : 'above', tip: isLower ? bar.low : bar.high, wick, range, wickPct, bodyHigh, bodyLow };
}

export type WickState = 'pending' | 'confirmed' | 'expired' | 'invalidated';

/** What later closed bars did to the mark at index i. */
export function wickLifecycle(
  bars: readonly WickBar[],
  i: number,
  g: Pick<WickGeometry, 'side' | 'tip' | 'bodyHigh' | 'bodyLow'>,
  confirmBars: number,
): { state: WickState; resolvedAt: number | null; barsAfter: number } {
  const last = Math.min(bars.length - 1, i + confirmBars);
  for (let j = i + 1; j <= last; j++) {
    const b = bars[j];
    if (g.side === 'below') {
      if (b.low < g.tip) return { state: 'invalidated', resolvedAt: b.time, barsAfter: j - i };
      if (b.close > g.bodyHigh) return { state: 'confirmed', resolvedAt: b.time, barsAfter: j - i };
    } else {
      if (b.high > g.tip) return { state: 'invalidated', resolvedAt: b.time, barsAfter: j - i };
      if (b.close < g.bodyLow) return { state: 'confirmed', resolvedAt: b.time, barsAfter: j - i };
    }
  }
  const after = bars.length - 1 - i;
  return { state: after >= confirmBars ? 'expired' : 'pending', resolvedAt: null, barsAfter: after };
}

export interface WickRejection extends WickGeometry {
  time: number;
  atr: number;
  close: number;
  volume: number;
  /** Levels the wick swept while the body held the other side. */
  levels: WickLevel[];
  state: WickState;
  /** Bar time that confirmed / invalidated it. */
  resolvedAt: number | null;
  confirmBars: number;
}

/**
 * Geometry, swept levels and lifecycle for every closed bar (ascending). The
 * volume factors are added by `gradeWicks`, which is cheap enough to run on
 * every Vest / delta update.
 */
export function computeWickRejections(
  closedBars: readonly WickBar[],
  settings: Partial<WickSettings>,
  vwap?: ReadonlyMap<number, number>,
): WickRejection[] {
  const s = clampWickSettings(settings);
  const out: WickRejection[] = [];
  let levels: WickLevel[][] | null = null;
  for (let i = 0; i < closedBars.length; i++) {
    const bar = closedBars[i];
    const atr = wickAtr(closedBars, i);
    const g = wickGeometry(bar, atr, s);
    if (!g || atr === null) continue;
    levels ??= levelsBeforeBars(closedBars, vwap);
    const swept = levels[i].filter((l) => (g.side === 'below' ? bar.low < l.price && l.price <= g.bodyLow : g.bodyHigh <= l.price && l.price < bar.high));
    const life = wickLifecycle(closedBars, i, g, s.confirmBars);
    out.push({
      ...g,
      time: bar.time,
      atr,
      close: bar.close,
      volume: bar.volume ?? 0,
      levels: swept,
      state: life.state,
      resolvedAt: life.resolvedAt,
      confirmBars: s.confirmBars,
    });
  }
  return out;
}

export interface GradedWick extends WickRejection {
  spike: 'vest' | 'cme' | 'both' | null;
  /** CME delta when it absorbed against the wick, else null. */
  absorptionDelta: number | null;
  strong: boolean;
}

export interface WickFactorInputs {
  vestSpikeTimes?: ReadonlySet<number>;
  cmeSpikeTimes?: ReadonlySet<number>;
  /** CME delta per chart bar (seconds). */
  deltas?: ReadonlyMap<number, number>;
}

/** True when `delta` leans into the wick on a bar of `volume` contracts. */
export function isAbsorption(side: MarkerSide, delta: number | null | undefined, volume: number): boolean {
  if (delta == null || !Number.isFinite(delta) || delta === 0) return false;
  const against = side === 'below' ? delta < 0 : delta > 0;
  if (!against) return false;
  return volume > 0 ? Math.abs(delta) >= WICK_ABSORPTION_MIN_FRAC * volume : true;
}

export function gradeWicks(wicks: readonly WickRejection[], f: WickFactorInputs): GradedWick[] {
  return wicks.map((w) => {
    const v = !!f.vestSpikeTimes?.has(w.time);
    const c = !!f.cmeSpikeTimes?.has(w.time);
    const spike = v && c ? 'both' : v ? 'vest' : c ? 'cme' : null;
    const d = f.deltas?.get(w.time);
    const absorptionDelta = isAbsorption(w.side, d, w.volume) ? (d as number) : null;
    return { ...w, spike, absorptionDelta, strong: w.levels.length > 0 || spike !== null || absorptionDelta !== null };
  });
}

/**
 * The marks to draw: with requireLevel only level sweeps; invalidated (broken)
 * ones only when brokenHooks is 'faded' (they are drawn small and faint).
 */
export function visibleWicks<T extends WickRejection>(wicks: readonly T[], requireLevel: boolean, brokenHooks: WickBrokenMode): T[] {
  return wicks.filter((w) => (brokenHooks === 'faded' || w.state !== 'invalidated') && (!requireLevel || w.levels.length > 0));
}

const pts = (v: number): string => v.toFixed(2);

/** Hover text: what fired, and where the mark stands. */
export function wickTooltip(w: GradedWick, emoji: string): { title: string; lines: string[] } {
  const up = w.side === 'below';
  const lines = [
    `${up ? 'Lower' : 'Upper'} wick ${pts(w.wick)} pts = ${Math.round(w.wickPct)}% of the range, ${(w.wick / w.atr).toFixed(1)}x ATR(${WICK_ATR_PERIOD})`,
  ];
  const fired: string[] = [];
  if (w.levels.length > 0) {
    fired.push(`Swept ${w.levels.map((l) => `${l.name} ${pts(l.price)}`).join(', ')} and closed back`);
  }
  if (w.spike) fired.push(w.spike === 'both' ? '🐋🦈 Vest and CME volume spike' : w.spike === 'vest' ? '🐋 Vest volume spike' : '🦈 CME volume spike');
  if (w.absorptionDelta !== null) {
    const d = Math.round(w.absorptionDelta);
    fired.push(`Absorption: CME delta ${d > 0 ? '+' : ''}${d} (net ${d < 0 ? 'selling' : 'buying'}) into the wick`);
  }
  lines.push(...(fired.length > 0 ? fired : ['No level, spike or absorption (base mark)']));
  const body = up ? pts(w.bodyHigh) : pts(w.bodyLow);
  if (w.state === 'invalidated') lines.push(`Broken: ${pts(w.tip)} traded before a close ${up ? 'above' : 'below'} ${body} confirmed it`);
  else if (w.state === 'confirmed') lines.push(`Confirmed: a later bar closed ${up ? 'above' : 'below'} the body (${body})`);
  else if (w.state === 'expired') lines.push(`Unconfirmed: no close ${up ? 'above' : 'below'} ${body} within ${w.confirmBars} bars`);
  else lines.push(`Waiting: a close ${up ? 'above' : 'below'} ${body} within ${w.confirmBars} bars confirms; ${pts(w.tip)} trading first breaks it`);
  lines.push('Closed bars only. Descriptive, not a signal.');
  const grade = `${w.strong ? 'strong' : 'base'}${w.state === 'invalidated' ? ', broken' : ''}`;
  return { title: `${emoji} ${up ? 'Lower' : 'Upper'} wick rejection, possible reversal ${up ? 'up' : 'down'} (${grade})`, lines };
}
